import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import LegacyHomeClient from '../../components/legacy-home-client';
import SharedGameFooter from '../../components/shared-game-footer';
import '../../components/legacy-shell-overrides.css';
import { buildLegacyShellMarkup, type LegacyView } from '../../lib/legacy-shell-markup';
import { channelByRoute } from '../../lib/wiki-data';
import { fallbackCatalogNavigation } from '../../lib/catalog-navigation';
import { useRegisteredGame } from '../use-registered-game';

function viewFromPathname(pathname: string): LegacyView {
  const route = pathname.split('/').filter(Boolean)[1];
  return route ? channelByRoute[route] || 'home' : 'home';
}

/**
 * SPA 游戏壳：对应 vinext 用户侧 LegacyGameHome(clientBootstrap) + 壳 tab 路由。
 * 等 /api/games 解析出 game_key 后再拉 homepage / catalog，避免用 URL 上的 domain_key 打接口 404。
 */
export default function SpaGameShellPage() {
  const params = useParams();
  const { pathname } = useLocation();
  const gameParam = String(params.game || '');
  const registered = useRegisteredGame(gameParam);
  // This built-in game's key and display name are known before the registry request.
  const initialArcana = gameParam === 'arcanatactics';
  const view = viewFromPathname(pathname);
  const domain =
    registered.status === 'ready'
      ? registered.game.domain || gameParam
      : gameParam;
  const gameKey =
    registered.status === 'ready'
      ? registered.game.key || gameParam
      : gameParam;
  const gameName =
    registered.status === 'ready'
      ? registered.game.name || gameParam
      : initialArcana ? '阿尔克纳' : gameParam;
  const gameIcon =
    registered.status === 'ready' ? registered.game.icon : undefined;

  const markup = useMemo(() => {
    return buildLegacyShellMarkup(
      view,
      [],
      domain,
      fallbackCatalogNavigation(),
      null,
    );
  }, [domain, view]);

  if (registered.status === 'error') {
    return (
      <main className="spa-boot" role="alert">
        <h1>加载失败</h1>
        <p>{registered.message}</p>
        <p>
          <a href="/">返回首页</a>
        </p>
      </main>
    );
  }

  return (
    <div className="game-page-layout">
      <div className="game-page-content">
        {registered.status === 'ready' || initialArcana ? (
          <LegacyHomeClient
            key={`${gameKey}:${domain}:${view}`}
            view={view}
            gameKey={gameKey}
            gameDomain={domain}
            gameName={gameName}
            gameIcon={gameIcon}
            heroDirectory={[]}
            bannerHeroes={[]}
            bannerLive2dSlots={[]}
            catalogNavigation={fallbackCatalogNavigation()}
            initialHomepageConfig={null}
            markup={markup}
            clientBootstrap
          />
        ) : (
          <div
            className="shared-game-shell shared-game-shell--home"
            data-game={gameParam}
          >
            {/* 游戏解析完成前先占住顶栏 banner 高度，避免正文顶到最上闪一下 */}
            <div
              className={`gk-hero${gameParam === 'arcanatactics' ? ' has-live2d hero-theme-light' : ''}`}
              aria-hidden="true"
            >
              {gameParam === 'arcanatactics' && <div className="gk-hero-bg" />}
            </div>
            <div className="shared-game-content" aria-busy="true">
              <div
                className="legacy-game-home"
                dangerouslySetInnerHTML={{ __html: markup }}
              />
            </div>
          </div>
        )}
      </div>
      <SharedGameFooter gameKey={domain} gameName={gameName} />
    </div>
  );
}
